/**
 * Member Modal Component
 * Form for adding/editing members
 */

import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { addMember, updateMember } from '../../redux/slices/memberSlice';
import { createMember, updateMember as updateMemberService, getAllMembers } from '../../services/memberService.firebase';
import { convertImageToBase64, validateImageFile } from '../../utils/imageUtils';
import ImageCropModal from '../common/ImageCropModal';
import './MemberModal.css';

const MemberModal = ({ member, onClose, onSuccess, onRefresh }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    gender: 'male',
    dateOfBirth: '',
    address: '',
    joinDate: new Date().toISOString().split('T')[0],
    status: 'active',
    membershipType: 'regular',
    maritalStatus: '',
    marriageDate: '',
    spouseId: '',
    spouseName: '',
    hasPortalAccess: false,
    profileImage: '',
  });
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [allMembers, setAllMembers] = useState([]);

  useEffect(() => {
    if (member) {
      setFormData(member);
      if (member.profileImage) {
        setImagePreview(member.profileImage);
      }
    }
    // Fetch all members for spouse selector
    fetchAllMembers();
  }, [member]);

  const fetchAllMembers = async () => {
    try {
      const members = await getAllMembers();
      setAllMembers(members || []);
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validation = validateImageFile(file);
    if (!validation.valid) {
      alert(validation.error);
      return;
    }

    try {
      const base64Image = await convertImageToBase64(file);
      setImageToCrop(base64Image);
      setShowCropModal(true);
    } catch (err) {
      alert(err.message || 'Failed to process image');
    }
  };

  const handleCropComplete = (croppedImage) => {
    setFormData({
      ...formData,
      profileImage: croppedImage,
    });
    setImagePreview(croppedImage);
    setShowCropModal(false);
    setImageToCrop(null);
  };

  const handleRemoveImage = () => {
    setFormData({
      ...formData,
      profileImage: '',
    });
    setImagePreview(null);
  };

  const handleCheckboxChange = (e) => {
    setFormData({
      ...formData,
      hasPortalAccess: e.target.checked,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (member) {
        const updated = await updateMemberService(member.id, formData);
        dispatch(updateMember(updated));
      } else {
        const created = await createMember(formData);
        dispatch(addMember(created));
        
        if (formData.hasPortalAccess && created.credentials) {
          alert(`✅ Member added successfully!\n\nPortal credentials have been automatically generated and emailed to ${formData.email}\n\nUsername: ${created.credentials.username}\nPassword: ${created.credentials.password}`);
        }
      }
      // Call the appropriate callback
      if (onSuccess) {
        onSuccess();
      } else if (onRefresh) {
        onRefresh();
      }
      onClose();
    } catch (error) {
      console.error('Error saving member:', error);
      alert('Failed to save member');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{member ? 'Edit Member' : 'Add New Member'}</h2>
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-row">
            <div className="form-group">
              <label>Full Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Phone *</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Gender *</label>
              <select name="gender" value={formData.gender} onChange={handleChange}>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Date of Birth</label>
              <input
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Join Date *</label>
              <input
                type="date"
                name="joinDate"
                value={formData.joinDate}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>Address</label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Status *</label>
              <select name="status" value={formData.status} onChange={handleChange}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="form-group">
              <label>Membership Type *</label>
              <select
                name="membershipType"
                value={formData.membershipType}
                onChange={handleChange}
              >
                <option value="regular">Regular</option>
                <option value="leadership">Leadership</option>
                <option value="volunteer">Volunteer</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Marital Status</label>
              <select
                name="maritalStatus"
                value={formData.maritalStatus}
                onChange={handleChange}
              >
                <option value="">Select Status</option>
                <option value="single">Single</option>
                <option value="married">Married</option>
                <option value="widowed">Widowed</option>
              </select>
            </div>

            {formData.maritalStatus === 'married' && (
              <>
                <div className="form-group">
                  <label>Marriage Date</label>
                  <input
                    type="date"
                    name="marriageDate"
                    value={formData.marriageDate}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Spouse (Link Profile)</label>
                  <select
                    name="spouseId"
                    value={formData.spouseId}
                    onChange={handleChange}
                  >
                    <option value="">-- No Spouse Linked --</option>
                    {allMembers.map((m) => (
                      <option key={m.id} value={m.id} disabled={m.id === member?.id}>
                        {m.name || `${m.firstName || ''} ${m.lastName || ''}`.trim()}
                      </option>
                    ))}
                  </select>
                  <small style={{color: '#718096', marginTop: '4px', display: 'block'}}>
                    Select spouse to show anniversaries together in slideshow
                  </small>
                </div>
                <div className="form-group">
                  <label>Spouse Name (if profile not created)</label>
                  <input
                    type="text"
                    name="spouseName"
                    placeholder="Enter spouse name"
                    value={formData.spouseName}
                    onChange={handleChange}
                  />
                  <small style={{color: '#718096', marginTop: '4px', display: 'block'}}>
                    If spouse doesn't have a profile yet, enter their name here
                  </small>
                </div>
              </>
            )}
          </div>

          <div className="form-group profile-image-upload">
            <label>Profile Picture (Optional)</label>
            <div className="image-upload-container">
              {imagePreview ? (
                <div className="image-preview">
                  <img src={imagePreview} alt="Profile preview" />
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="remove-image-btn"
                    disabled={loading}
                  >
                    ✕ Remove
                  </button>
                </div>
              ) : (
                <div className="image-upload-placeholder">
                  <input
                    type="file"
                    id="profileImage"
                    accept="image/jpeg,image/png,image/jpg,image/gif,image/webp"
                    onChange={handleImageChange}
                    disabled={loading}
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="profileImage" className="upload-label">
                    <span className="upload-icon">📷</span>
                    <span>Upload Photo</span>
                    <span className="upload-hint">Max 2MB • JPG, PNG, GIF, WebP</span>
                  </label>
                </div>
              )}
            </div>
          </div>

          {!member && (
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.hasPortalAccess}
                  onChange={handleCheckboxChange}
                />
                <span>Create member portal access and send credentials via email</span>
              </label>
              {formData.hasPortalAccess && (
                <p className="help-text">
                  ✅ A secure username and password will be generated and emailed to the member automatically.
                </p>
              )}
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : member ? 'Update' : 'Add Member'}
            </button>
          </div>
        </form>

        {showCropModal && imageToCrop && (
          <ImageCropModal
            image={imageToCrop}
            onCrop={handleCropComplete}
            onCancel={() => {
              setShowCropModal(false);
              setImageToCrop(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default MemberModal;
